<template>
    <div id="page_subspace_manage" class="ssm">
        <div class="ssm-main-layout">
            <header class="ssm-project-name-bar">
                <span class="ssm-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <div class="ssm-project-name-main">
                    <h1 class="ssm-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
                    <div class="ssm-project-meta">
                        <span class="ssm-project-type-tag" :class="spaceMode">{{ projectTypeLabel }}</span>
                        <span class="ssm-project-type-hint">{{ projectTypeHint }}</span>
                    </div>
                </div>
            </header>

            <div v-if="!isSupportedProject" class="ssm-panel">
                <div class="ssm-unsupported">
                    <el-empty description="当前项目类型暂不支持子空间管理（仅研伴项目、结对项目可用）" />
                </div>
            </div>

            <div v-else class="ssm-panel">
                <div class="ssm-panel-header">
                    <div class="ssm-panel-stats">
                        <span class="ssm-stat">校级空间 <em>{{ schoolCount }}</em> 个</span>
                        <span class="ssm-stat">子空间 <em>{{ subCount }}</em> 个</span>
                    </div>
                    <div class="ssm-panel-actions">
                        <button type="button" class="ssm-btn ssm-btn-primary ssm-btn-sm" @click="openImportDialog">
                            批量导入
                        </button>
                        <button type="button" class="ssm-btn ssm-btn-primary ssm-btn-sm" @click="openCreateDialog">
                            {{ createBtnText }}
                        </button>
                    </div>
                </div>

                <el-form :inline="true" :model="filters" class="ssm-filter-bar" @submit.native.prevent>
                    <div class="ssm-filter-left">
                        <el-form-item>
                            <el-select v-model="filters.school" size="small" clearable filterable placeholder="全部校级空间"
                                style="width: 240px;" @change="onFilterChange">
                                <el-option label="全部校级空间" value="" />
                                <el-option v-for="s in schoolSpaceOptions" :key="s.id" :label="s.name" :value="s.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="filters.origin" size="small" clearable placeholder="全部创建来源"
                                style="width: 156px;" @change="onFilterChange">
                                <el-option label="全部创建来源" value="" />
                                <el-option label="项目管理员创建" value="project" />
                                <el-option label="校级管理员创建" value="school" />
                            </el-select>
                        </el-form-item>
                    </div>
                    <el-form-item class="ssm-filter-search">
                        <el-input v-model.trim="filters.search" size="small" clearable class="ssm-search-input"
                            placeholder="搜索校级空间、子空间、学校、管理员、班级或成员…" suffix-icon="el-icon-search" />
                    </el-form-item>
                </el-form>

                <div class="ssm-panel-body" v-loading="listLoading">
                    <div v-if="filteredGroups.length" class="ssm-nested-scroll">
                        <div v-for="row in filteredGroups" :key="row.id" class="ssm-school-block">
                            <div class="ssm-school-row"
                                :class="{ 'is-open': expandedSchoolId === row.id, 'is-expandable': row.children.length }"
                                @click="toggleExpand(row)">
                                <div class="ssm-school-row-main">
                                    <button v-if="row.children.length" type="button" class="ssm-expand-btn"
                                        :class="{ 'is-open': expandedSchoolId === row.id }" tabindex="-1">
                                        <i class="el-icon-arrow-right"></i>
                                    </button>
                                    <span v-else class="ssm-expand-placeholder"></span>
                                    <div class="ssm-school-info">
                                        <div class="ssm-school-title-row">
                                            <span class="ssm-school-title" :title="row.name">{{ row.name }}</span>
                                            <span class="ssm-school-type-tag">校级空间</span>
                                        </div>
                                        <div v-if="(row.schools || []).length" class="ssm-school-chips">
                                            <span v-for="(school, si) in row.schools" :key="si"
                                                class="ssm-school-chip">{{ school }}</span>
                                        </div>
                                        <div class="ssm-school-admins">管理员：{{ adminDisplay(row) }}</div>
                                    </div>
                                </div>
                                <div class="ssm-school-row-side">
                                    <div class="ssm-school-time">{{ formatMinuteTime(row.createdAt) }}</div>
                                    <button type="button" class="ssm-btn ssm-btn-primary ssm-btn-sm"
                                        @click.stop="openMomentsDialog(row)">精彩瞬间管理</button>
                                    <div class="ssm-school-child-count">子空间 {{ row.childTotal }}</div>
                                </div>
                            </div>
                            <div v-show="expandedSchoolId === row.id" class="ssm-child-list">
                                <div v-for="child in row.children" :key="child.id" class="ssm-child-row">
                                    <div class="ssm-child-main">
                                        <div class="ssm-child-title-row">
                                            <span class="ssm-child-title">{{ child.name }}</span>
                                            <span class="ssm-child-type-tag">{{ childTypeLabel }}</span>
                                        </div>
                                        <div class="ssm-child-meta">
                                            <span class="ssm-meta-label">创建来源</span>
                                            <span>{{ child.origin === 'school' ? '校级管理员' : '项目管理员' }}</span>
                                            <span class="ssm-meta-gap"></span>
                                            <template v-if="isPairMode">
                                                <span class="ssm-meta-label">指导教师</span>
                                                <span>{{ child.teachers || '—' }}</span>
                                            </template>
                                            <template v-else>
                                                <span class="ssm-meta-label">学科负责人</span>
                                                <span>{{ child.leader || '—' }}</span>
                                            </template>
                                        </div>
                                        <div class="ssm-child-meta">
                                            <template v-if="isPairMode">
                                                <span class="ssm-meta-label">班级</span>
                                                <span>{{ classGroupText(child) || '—' }}</span>
                                            </template>
                                            <template v-else>
                                                <span class="ssm-meta-label">成员</span>
                                                <span>{{ memberText(child) }}</span>
                                            </template>
                                        </div>
                                    </div>
                                    <div class="ssm-child-side">
                                        <div class="ssm-child-actions">
                                            <button type="button" class="ssm-child-btn"
                                                @click="openEditDialog(child)">编辑</button>
                                            <button type="button" class="ssm-child-btn"
                                                @click="openMomentsDialog(child)">精彩瞬间管理</button>
                                            <button type="button" class="ssm-child-btn"
                                                @click="onDelete(child)">删除</button>
                                        </div>
                                        <div class="ssm-child-time">{{ formatMinuteTime(child.createdAt) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <el-empty v-else-if="!listLoading" :description="emptyDesc" />
                </div>
                <pagination :total="pages.total" :page.sync="pages.currentPage" :limit.sync="pages.pageSize"
                    :pageSizes="[10, 20, 30]" :hide-on-single-page="true" @pagination="handlePageChange" />
            </div>
        </div>

        <!-- 创建 / 编辑 -->
        <el-dialog :title="formDialogTitle" :visible.sync="formDialogVisible" width="640px" append-to-body
            custom-class="ssm-form-dialog" destroy-on-close @closed="resetForm">
            <el-form class="ssm-form" label-position="top" v-loading="formOptionsLoading" @submit.native.prevent>
                <el-form-item required>
                    <template slot="label">校级空间 <span class="ssm-required">*</span></template>
                    <p class="ssm-form-hint">{{ schoolSpaceHint }}</p>
                    <el-select v-model="form.schoolSpaceId" placeholder="请选择校级空间" filterable style="width:100%;"
                        :disabled="editMode" @change="onFormSchoolChange">
                        <el-option v-for="s in schoolSpaceOptions" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item required>
                    <template slot="label">空间名称 <span class="ssm-required">*</span></template>
                    <el-input v-model.trim="form.name" :placeholder="namePlaceholder" maxlength="50" clearable />
                </el-form-item>

                <template v-if="isPairMode">
                    <el-form-item required>
                        <template slot="label">结对学校与班级 <span class="ssm-required">*</span></template>
                        <p class="ssm-form-hint">请至少选择 2 个班级。</p>
                        <div v-if="!form.schoolSpaceId" class="ssm-form-hint">请先选择校级空间</div>
                        <div v-else-if="!pairClassGroups.length" class="ssm-form-hint">暂无可选班级</div>
                        <div v-else class="ssm-class-groups">
                            <div v-for="group in pairClassGroups" :key="group.schoolId" class="ssm-class-group">
                                <div class="ssm-class-group-title">{{ group.schoolName }}</div>
                                <el-checkbox-group v-model="form.classIds" class="ssm-check-grid">
                                    <label v-for="item in group.classes" :key="item.id" class="ssm-check-item">
                                        <el-checkbox :label="item.id">{{ item.name }}</el-checkbox>
                                    </label>
                                </el-checkbox-group>
                            </div>
                        </div>
                    </el-form-item>
                    <el-form-item label="指导教师">
                        <el-select v-model="form.teacherIds" multiple filterable clearable placeholder="请选择指导教师"
                            style="width:100%;">
                            <el-option v-for="u in userOptions" :key="u.userId" :label="userOptionLabel(u)"
                                :value="u.userId" />
                        </el-select>
                    </el-form-item>
                </template>

                <template v-else>
                    <el-form-item label="学科负责人">
                        <el-select v-model="form.leaderIds" multiple filterable clearable placeholder="请选择学科负责人"
                            style="width:100%;">
                            <el-option v-for="u in userOptions" :key="'l-' + u.userId" :label="userOptionLabel(u)"
                                :value="u.userId" />
                        </el-select>
                    </el-form-item>
                    <el-form-item required>
                        <template slot="label">师傅（可多选） <span class="ssm-required">*</span></template>
                        <el-select v-model="form.mentorIds" multiple filterable clearable placeholder="请选择师傅"
                            style="width:100%;">
                            <el-option v-for="u in userOptions" :key="'m-' + u.userId" :label="userOptionLabel(u)"
                                :value="u.userId" />
                        </el-select>
                    </el-form-item>
                    <el-form-item required>
                        <template slot="label">徒弟（可多选） <span class="ssm-required">*</span></template>
                        <el-select v-model="form.menteeIds" multiple filterable clearable placeholder="请选择徒弟"
                            style="width:100%;">
                            <el-option v-for="u in userOptions" :key="'e-' + u.userId" :label="userOptionLabel(u)"
                                :value="u.userId" />
                        </el-select>
                    </el-form-item>
                </template>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="formDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="formSaving" @click="submitForm">
                    {{ editMode ? '保存修改' : '确定创建' }}
                </el-button>
            </span>
        </el-dialog>

        <!-- 批量导入 -->
        <el-dialog title="批量导入子空间" :visible.sync="importDialogVisible" width="640px" append-to-body
            custom-class="ssm-import-dialog" destroy-on-close @closed="resetImport">
            <div class="ssm-import-steps">
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">1</span>下载导入模板
                    </div>
                    <p class="ssm-import-desc">{{ importDesc }}</p>
                    <ul class="ssm-import-tpl-fields">
                        <li v-html="importFieldsHtml" />
                    </ul>
                    <el-button size="small" icon="el-icon-download" style="margin-top:10px;"
                        @click="downloadTemplate">下载结对班级导入模板（.xlsx）</el-button>
                        <el-button size="small" icon="el-icon-download" style="margin-top:10px;"
                        @click="downloadTemplate2">下载研伴空间导入模板（.xlsx）</el-button>
                </div>
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">2</span>上传填写好的文件
                    </div>
                    <p class="ssm-import-desc">请使用本页下载的 Excel 模板填写后上传（.xlsx）。</p>
                    <div class="ssm-upload-zone" :class="{ dragover: importDragover }" tabindex="0" role="button"
                        @click="$refs.importFile.click()" @keydown.enter.prevent="$refs.importFile.click()"
                        @dragenter.prevent="importDragover = true" @dragover.prevent="importDragover = true"
                        @dragleave.prevent="importDragover = false" @drop.prevent="onImportDrop">
                        <div class="ssm-uz-icon">📄</div>
                        <div class="ssm-uz-title">点击选择文件，或拖拽到此处</div>
                        <div class="ssm-uz-hint">支持 .xlsx</div>
                        <div v-if="importFileName" class="ssm-uz-file">已选择：{{ importFileName }}</div>
                    </div>
                    <input ref="importFile" type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden
                        @change="onImportFileChange">
                </div>
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">3</span>导入结果
                    </div>
                    <div v-if="!importResult" class="ssm-import-empty">选择文件并点击「确认导入」后将在此展示结果</div>
                    <template v-else>
                        <div class="ssm-import-summary">
                            <span class="ssm-sum-item ok">成功 {{ importResult.successCount || 0 }}</span>
                            <span class="ssm-sum-item err">失败 {{ importResult.failCount || 0 }}</span>
                        </div>
                        <div class="ssm-import-preview-wrap">
                            <div v-if="!(importResult.errors && importResult.errors.length)" class="ssm-import-empty">
                                全部导入成功
                            </div>
                            <table v-else class="ssm-import-preview">
                                <thead>
                                    <tr>
                                        <th style="width:60px;">行号</th>
                                        <th>空间名称</th>
                                        <th>失败原因</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(err, idx) in importResult.errors" :key="idx" class="is-invalid">
                                        <td>{{ err.row }}</td>
                                        <td>{{ err.spxName || '—' }}</td>
                                        <td class="row-err">{{ err.reason || '—' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="importDialogVisible = false">取 消</el-button>
                <el-button type="primary" :disabled="!importFile" :loading="importSaving"
                    @click="confirmImport">确认导入</el-button>
            </span>
        </el-dialog>

        <!-- 精彩瞬间管理 -->
        <el-dialog title="精彩瞬间管理" :visible.sync="momentsDialogVisible" width="560px" append-to-body
            custom-class="ssm-moments-dialog" destroy-on-close @closed="resetMomentsForm">
            <el-form class="ssm-form" label-position="top" v-loading="momentsLoading" @submit.native.prevent>
                <el-form-item required>
                    <template slot="label">名称 <span class="ssm-required"></span></template>
                    <el-input v-model.trim="momentsForm.name" maxlength="50" placeholder="请输入名称" clearable />
                </el-form-item>
                <el-form-item required>
                    <template slot="label">链接地址 <span class="ssm-required"></span></template>
                    <el-input v-model.trim="momentsForm.url" placeholder="请输入链接地址，如 https://example.com/moments"
                        clearable />
                </el-form-item>
                <el-form-item v-if="!isMomentsSchoolSpace" required>
                    <template slot="label">是否同步展示在校级空间 <span class="ssm-required"></span></template>
                    <el-radio-group v-model="momentsForm.syncSchool" class="ssm-radio-row">
                        <el-radio :label="1">是</el-radio>
                        <el-radio :label="0">否</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="momentsDialogVisible = false">取 消</el-button>
                <el-button type="primary" :disabled="momentsLoading" :loading="momentsSaving"
                    @click="saveMomentsConfig">保 存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import pagination from '@/common/community/pagination.vue';
    import {
        mapState
    } from 'vuex';

    /** modelType：4 研伴项目，5 结对项目 */
    const MODEL_TYPE = {
        YANBAN: 4,
        PAIR: 5,
    };

    /** 创建来源：1 校级管理员 2 项目管理员（本页创建固定传 2） */
    const CREATE_SOURCE_PROJECT = 2;

    const CREATE_TYPE = {
        PAIR: 3,
        YANBAN: 4,
    };

    const PROJECT_META = {
        pair: {
            label: '结对项目',
            hint: '本项目仅可创建班级空间',
            createBtn: '＋ 创建班级空间',
            createTitle: '创建班级空间',
            editTitle: '编辑班级空间',
            schoolHint: '请选择本子空间所属的校级结对空间。',
            namePlaceholder: '如：初一（2）班结对空间',
            searchPlaceholder: '搜索空间名称、班级、指导教师…',
            importDesc: '当前为结对项目，请下载模板填写班级空间信息后上传。',
            importFieldsHtml: '<strong>班级空间：</strong>空间类型、空间名称、绑定的校级结对空间名称、结对班级所在学校、结对班级、指导教师姓名/手机号等',
        },
        yanban: {
            label: '研伴项目',
            hint: '本项目仅可创建研伴空间',
            createBtn: '＋ 创建研伴空间',
            createTitle: '创建研伴空间',
            editTitle: '编辑研伴空间',
            schoolHint: '请选择本子空间所属的校级研伴空间。',
            namePlaceholder: '如：曹靖人 · 李明研伴空间',
            searchPlaceholder: '搜索空间名称、成员、学科负责人…',
            importDesc: '当前为研伴项目，请下载模板填写研伴空间信息后上传。',
            importFieldsHtml: '<strong>研伴空间：</strong>空间类型、空间名称、绑定的校级结对空间名称、学科负责人/师傅/徒弟姓名与手机号等',
        },
    };

    const isApiOk = (res) => res && (res.code === 0 || res.code === 200);

    export default {
        name: 'SubspaceManage',
        components: {
            pagination,
        },
        data() {
            return {
                listLoading: false,
                list: [],
                childrenMap: {},
                expandedSchoolId: null,
                schoolCount: 0,
                subCount: 0,
                schoolSpaceOptions: [],
                filters: {
                    school: '',
                    origin: '',
                    search: '',
                },
                pages: {
                    currentPage: 1,
                    pageSize: 10,
                    total: 0,
                },
                formDialogVisible: false,
                formSaving: false,
                formOptionsLoading: false,
                editMode: false,
                editingId: null,
                editingCreateSource: CREATE_SOURCE_PROJECT,
                form: {
                    schoolSpaceId: '',
                    name: '',
                    classIds: [],
                    teacherIds: [],
                    leaderIds: [],
                    mentorIds: [],
                    menteeIds: [],
                },
                pairClassGroups: [],
                userOptions: [],
                importDialogVisible: false,
                importDragover: false,
                importFileName: '',
                importFile: null,
                importResult: null,
                importSaving: false,
                momentsDialogVisible: false,
                momentsLoading: false,
                momentsSaving: false,
                momentsTarget: null,
                momentsForm: {
                    id: null,
                    name: '精彩瞬间',
                    url: '',
                    syncSchool: 0,
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            modelType() {
                const t = this.currentProject && this.currentProject.modelType;
                return t == null || t === '' ? null : Number(t);
            },
            spaceMode() {
                if (this.modelType === MODEL_TYPE.YANBAN) return 'yanban';
                if (this.modelType === MODEL_TYPE.PAIR) return 'pair';
                return '';
            },
            isSupportedProject() {
                return this.spaceMode === 'pair' || this.spaceMode === 'yanban';
            },
            isPairMode() {
                return this.spaceMode === 'pair';
            },
            childTypeLabel() {
                return this.isPairMode ? '班级结对空间' : '研伴空间';
            },
            createType() {
                return this.isPairMode ? CREATE_TYPE.PAIR : CREATE_TYPE.YANBAN;
            },
            meta() {
                return PROJECT_META[this.spaceMode] || PROJECT_META.pair;
            },
            projectTypeLabel() {
                if (!this.isSupportedProject) {
                    const map = {
                        1: '教师类',
                        2: '学生类',
                        3: '其他',
                        4: '研伴项目',
                        5: '结对项目',
                        6: '三交活动',
                        7: '大赛活动',
                        8: '三段九环',
                    };
                    return map[this.modelType] || '未知类型';
                }
                return this.meta.label;
            },
            projectTypeHint() {
                if (!this.isSupportedProject) return '子空间管理仅支持研伴项目与结对项目';
                return this.meta.hint;
            },
            createBtnText() {
                return this.meta.createBtn;
            },
            schoolSpaceHint() {
                return this.meta.schoolHint;
            },
            namePlaceholder() {
                return this.meta.namePlaceholder;
            },
            importDesc() {
                return this.meta.importDesc;
            },
            importFieldsHtml() {
                return this.meta.importFieldsHtml;
            },
            formDialogTitle() {
                return this.editMode ? this.meta.editTitle : this.meta.createTitle;
            },
            emptyDesc() {
                const hasFilter = !!(this.filters.school || this.filters.origin || (this.filters.search || '').trim());
                if (hasFilter) return '暂无匹配结果';
                if (!this.list.length) return '暂无校级空间';
                return this.isPairMode ? '暂无班级空间，请点击「创建班级空间」新建' : '暂无研伴空间，请点击「创建研伴空间」新建';
            },
            momentsTargetName() {
                return (this.momentsTarget && this.momentsTarget.name) || '当前子空间';
            },
            isMomentsSchoolSpace() {
                return !!(this.momentsTarget && this.momentsTarget.isSchoolSpace);
            },
            filteredGroups() {
                const q = (this.filters.search || '').trim().toLowerCase();
                const origin = this.filters.origin;
                return this.list.map((school) => {
                    const allChildren = this.childrenMap[school.id] || [];
                    const originChildren = origin
                        ? allChildren.filter((child) => child.origin === origin)
                        : allChildren;
                    const matchedChildren = q
                        ? originChildren.filter((child) => this.childSearchText(child).includes(q))
                        : originChildren;
                    const schoolMatch = !q || this.schoolSearchText(school).includes(q);
                    const children = schoolMatch ? originChildren : matchedChildren;
                    return {
                        ...school,
                        children,
                        childTotal: allChildren.length,
                    };
                }).filter((school) => {
                    if (this.filters.school && String(school.id) !== String(this.filters.school)) return false;
                    if (!q) return true;
                    return this.schoolSearchText(school).includes(q) || school.children.length > 0;
                });
            },
        },
        watch: {
            'currentProject.id'() {
                this.pages.currentPage = 1;
                this.expandedSchoolId = null;
                this.resetFilters();
                this.initPage();
            },
            'currentProject.modelType'() {
                this.pages.currentPage = 1;
                this.expandedSchoolId = null;
                this.resetFilters();
                this.initPage();
            },
            'filters.search'() {
                this.syncExpanded(false);
            },
        },
        mounted() {
            this.initPage();
        },
        methods: {
            isApiOk,
            resetFilters() {
                this.filters = {
                    school: '',
                    origin: '',
                    search: '',
                };
            },
            schoolSearchText(row) {
                return [
                    row.name,
                    ...(row.schools || []),
                    ...(row.admins || []),
                ].join(' ').toLowerCase();
            },
            childSearchText(row) {
                const classText = (row.classes || []).map((c) => {
                    if (c == null) return '';
                    if (typeof c === 'string') return c;
                    return `${c.schoolName || ''} ${c.className || ''}`;
                }).join(' ');
                return [
                    row.name,
                    row.teachers,
                    row.leader,
                    row.schoolSpaceName,
                    classText,
                    ...(row.mentors || []),
                    ...(row.mentees || []),
                ].join(' ').toLowerCase();
            },
            adminDisplay(row) {
                const list = row.admins || [];
                return list.length ? list.join('、') : '—';
            },
            classGroupText(row) {
                const groups = [];
                const map = {};
                (row.classes || []).forEach((c) => {
                    if (c == null) return;
                    if (typeof c === 'string') {
                        groups.push({ school: '', names: [c] });
                        return;
                    }
                    const school = c.schoolName || '';
                    const name = c.className || '';
                    if (!map[school]) {
                        map[school] = { school, names: [] };
                        groups.push(map[school]);
                    }
                    if (name) map[school].names.push(name);
                });
                return groups.map((g) => {
                    const names = g.names.join('、');
                    return g.school ? `${g.school}${names}` : names;
                }).filter(Boolean).join('；');
            },
            formatMinuteTime(val) {
                if (!val) return '—';
                const s = String(val).replace('T', ' ').trim();
                const m = s.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})/);
                return m ? m[1] : s;
            },
            toggleExpand(row) {
                if (!(row.children && row.children.length)) return;
                this.expandedSchoolId = this.expandedSchoolId === row.id ? null : row.id;
            },
            syncExpanded(forceDefault) {
                const groups = this.filteredGroups;
                const current = groups.find((g) => String(g.id) === String(this.expandedSchoolId));
                if (current && current.children.length) return;
                if (!forceDefault && this.expandedSchoolId === null) return;
                const first = groups.find((g) => g.children.length);
                this.expandedSchoolId = first ? first.id : null;
            },
            memberText(row) {
                const mentors = (row.mentors || []).length;
                const mentees = (row.mentees || []).length;
                return `师傅 ${mentors} · 徒弟 ${mentees}`;
            },
            userOptionLabel(u) {
                const name = u.name || u.userName || '';
                const school = u.schoolName ? ` · ${u.schoolName}` : '';
                return `${name}${school}`;
            },
            getSchoolName(id) {
                const hit = this.schoolSpaceOptions.find((s) => String(s.id) === String(id));
                return hit ? hit.name : '';
            },
            extractNameList(list, nameKeys) {
                if (!Array.isArray(list) || !list.length) return [];
                const keys = nameKeys || ['name'];
                return list.map((item) => {
                    if (item == null) return '';
                    if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
                    for (let i = 0; i < keys.length; i += 1) {
                        const val = item[keys[i]];
                        if (val != null && String(val).trim()) return String(val).trim();
                    }
                    return '';
                }).filter(Boolean);
            },
            handlePageChange(data) {
                this.pages.currentPage = data.pageIndex;
                this.pages.pageSize = data.pageSize;
                this.fetchNestedList();
            },
            onFilterChange() {
                this.pages.currentPage = 1;
                this.fetchNestedList();
            },
            async initPage() {
                if (!this.isSupportedProject) {
                    this.list = [];
                    this.childrenMap = {};
                    this.schoolSpaceOptions = [];
                    this.schoolCount = 0;
                    this.subCount = 0;
                    this.pages.total = 0;
                    this.expandedSchoolId = null;
                    return;
                }
                await this.fetchSchoolSpaces();
                await Promise.all([
                    this.fetchNestedList(),
                    this.fetchSchoolCountOnly(),
                    this.fetchSubCountOnly(),
                ]);
            },
            refreshAfterChange() {
                return Promise.all([
                    this.fetchNestedList(),
                    this.fetchSubCountOnly(),
                ]);
            },
            async fetchNestedList() {
                if (!this.isSupportedProject) {
                    this.list = [];
                    this.childrenMap = {};
                    this.pages.total = 0;
                    return;
                }
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.list = [];
                    this.childrenMap = {};
                    this.pages.total = 0;
                    return;
                }
                this.listLoading = true;
                try {
                    const schoolFiltered = !!this.filters.school;
                    const res = await this.$Api.Paired.getSchoolPage({
                        projectId,
                        page: schoolFiltered ? 1 : this.pages.currentPage,
                        limit: schoolFiltered ? 999 : this.pages.pageSize,
                    });
                    if (!isApiOk(res)) {
                        this.list = [];
                        this.childrenMap = {};
                        this.pages.total = 0;
                        this.$message.error((res && res.msg) || '获取校级空间列表失败');
                        return;
                    }
                    const pageData = res.data || {};
                    let records = pageData.records || [];
                    if (schoolFiltered) {
                        records = records.filter((item) => String(item.spxId) === String(this.filters.school));
                        this.pages.total = records.length;
                    } else {
                        this.pages.total = pageData.total || 0;
                    }
                    this.list = records.map((item) => ({
                        id: item.spxId,
                        name: item.spxName || '',
                        schools: this.extractNameList(item.schools, ['schoolName', 'name']),
                        admins: this.extractNameList(item.admins, ['name', 'userName', 'adminName']),
                        origin: Number(item.createSource) === 1 ? 'school' : 'project',
                        createSource: item.createSource,
                        createdAt: item.creatAt || '',
                        isSchoolSpace: true,
                    }));
                    await this.fetchChildrenForSchools();
                    this.syncExpanded(true);
                } catch (e) {
                    this.list = [];
                    this.childrenMap = {};
                    this.pages.total = 0;
                    this.$message.error('获取校级空间列表失败');
                } finally {
                    this.listLoading = false;
                }
            },
            async fetchChildrenForSchools() {
                const parentIds = this.list.map((s) => s.id).filter(Boolean);
                if (!parentIds.length) {
                    this.childrenMap = {};
                    return;
                }
                try {
                    const res = await this.$Api.Paired.getChildrenPage({
                        parentIds: parentIds.join(','),
                        createType: this.createType,
                        page: 1,
                        limit: 999,
                    });
                    if (!isApiOk(res)) {
                        this.childrenMap = {};
                        return;
                    }
                    const records = (res.data && res.data.records) || [];
                    const rows = records.map((item) => ({
                        id: item.spxId,
                        type: this.spaceMode,
                        origin: Number(item.createSource) === 1 ? 'school' : 'project',
                        createSource: item.createSource,
                        schoolSpaceId: item.parentId,
                        schoolSpaceName: this.getSchoolName(item.parentId),
                        name: item.spxName || '',
                        classes: [],
                        classIds: [],
                        teachers: '',
                        teacherIds: [],
                        leader: '',
                        leaderId: '',
                        mentors: [],
                        mentorIds: [],
                        mentees: [],
                        menteeIds: [],
                        createdAt: item.creatAt || '',
                        isSchoolSpace: false,
                    }));
                    const enriched = await this.enrichList(rows);
                    const map = {};
                    enriched.forEach((child) => {
                        const pid = child.schoolSpaceId;
                        if (!map[pid]) map[pid] = [];
                        map[pid].push(child);
                    });
                    this.childrenMap = map;
                } catch (e) {
                    this.childrenMap = {};
                }
            },
            async fetchSchoolSpaces() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.schoolSpaceOptions = [];
                    return;
                }
                try {
                    const res = await this.$Api.Paired.getListByProject({
                        page: 1,
                        limit: 999,
                        projectId,
                        createType: 1,
                    });
                    if (!isApiOk(res)) {
                        this.schoolSpaceOptions = [];
                        this.$message.error((res && res.msg) || '获取校级空间失败');
                        return;
                    }
                    const records = (res.data && res.data.records) || [];
                    this.schoolSpaceOptions = records.map((item) => ({
                        id: item.id,
                        name: item.spxName || '',
                    }));
                } catch (e) {
                    this.schoolSpaceOptions = [];
                    this.$message.error('获取校级空间失败');
                }
            },
            async fetchSchoolCountOnly() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.schoolCount = 0;
                    return;
                }
                try {
                    const res = await this.$Api.Paired.getSchoolPage({
                        projectId,
                        page: 1,
                        limit: 1,
                    });
                    if (isApiOk(res)) {
                        this.schoolCount = (res.data && res.data.total) || 0;
                    }
                } catch (e) {
                    // 角标失败不影响主列表
                }
            },
            async fetchSubCountOnly() {
                const parentIds = this.schoolSpaceOptions.map((s) => s.id);
                if (!parentIds.length) {
                    this.subCount = 0;
                    return;
                }
                try {
                    const res = await this.$Api.Paired.getChildrenPage({
                        parentIds: parentIds.join(','),
                        createType: this.createType,
                        page: 1,
                        limit: 1,
                    });
                    if (isApiOk(res)) {
                        this.subCount = (res.data && res.data.total) || 0;
                    }
                } catch (e) {
                    // 角标失败不影响主列表
                }
            },
            async enrichList(rows) {
                if (!rows.length) return rows;
                const parentIds = [...new Set(rows.map((r) => r.schoolSpaceId).filter(Boolean))];
                const detailMap = {};
                await Promise.all(parentIds.map(async (parentId) => {
                    try {
                        if (this.isPairMode) {
                            const res = await this.$Api.Paired.pageSpxClassByParent({
                                parentId,
                                page: 1,
                                limit: 200,
                                createTypes: [CREATE_TYPE.PAIR],
                            });
                            if (!isApiOk(res)) return;
                            ((res.data && res.data.records) || []).forEach((item) => {
                                detailMap[item.spxId] = {
                                    classes: (item.classes || []).map((c) => ({
                                        schoolName: c.schoolName || '',
                                        className: c.className || '',
                                    })),
                                    classIds: (item.classes || []).map((c) => c.classId),
                                    teachers: (item.teachers || []).map((t) => t.userName).filter(Boolean).join('、'),
                                    teacherIds: (item.teachers || []).map((t) => t.userId),
                                };
                            });
                        } else {
                            const res = await this.$Api.Paired.getPartnerList({
                                parentId,
                                page: 1,
                                limit: 200,
                            });
                            if (!isApiOk(res)) return;
                            ((res.data && res.data.records) || []).forEach((item) => {
                                const leaders = item.leaders || [];
                                const masters = item.masters || [];
                                const apprentices = item.apprentices || [];
                                detailMap[item.spxId] = {
                                    leader: leaders.map((l) => l.name).filter(Boolean).join('、'),
                                    leaderIds: leaders.map((l) => l.userId).filter(Boolean),
                                    mentors: masters.map((m) => m.name).filter(Boolean),
                                    mentorIds: masters.map((m) => m.userId),
                                    mentees: apprentices.map((a) => a.name).filter(Boolean),
                                    menteeIds: apprentices.map((a) => a.userId),
                                };
                            });
                        }
                    } catch (e) {
                        // 详情补充失败时仍展示基础列表
                    }
                }));
                return rows.map((r) => ({
                    ...r,
                    ...(detailMap[r.id] || {}),
                }));
            },
            resetForm() {
                this.editMode = false;
                this.editingId = null;
                this.editingCreateSource = CREATE_SOURCE_PROJECT;
                this.formSaving = false;
                this.formOptionsLoading = false;
                this.pairClassGroups = [];
                this.userOptions = [];
                this.form = {
                    schoolSpaceId: '',
                    name: '',
                    classIds: [],
                    teacherIds: [],
                    leaderIds: [],
                    mentorIds: [],
                    menteeIds: [],
                };
            },
            openCreateDialog() {
                this.resetForm();
                this.formDialogVisible = true;
            },
            async openEditDialog(row) {
                this.editMode = true;
                this.editingId = row.id;
                this.editingCreateSource = row.createSource != null
                    ? Number(row.createSource)
                    : (row.origin === 'school' ? 1 : CREATE_SOURCE_PROJECT);
                this.form = {
                    schoolSpaceId: row.schoolSpaceId || '',
                    name: row.name || '',
                    classIds: [...(row.classIds || [])],
                    teacherIds: [...(row.teacherIds || [])],
                    leaderIds: [...(row.leaderIds || [])],
                    mentorIds: [...(row.mentorIds || [])],
                    menteeIds: [...(row.menteeIds || [])],
                };
                this.formDialogVisible = true;
                if (this.form.schoolSpaceId) {
                    await this.loadFormOptions(this.form.schoolSpaceId);
                }
            },
            async onFormSchoolChange(spxId) {
                this.form.classIds = [];
                this.form.teacherIds = [];
                this.form.leaderIds = [];
                this.form.mentorIds = [];
                this.form.menteeIds = [];
                this.pairClassGroups = [];
                this.userOptions = [];
                if (spxId) await this.loadFormOptions(spxId);
            },
            async loadFormOptions(spxId) {
                this.formOptionsLoading = true;
                try {
                    if (this.isPairMode) {
                        const [classRes, userRes] = await Promise.all([
                            this.$Api.Paired.getClassesBySchools({ spxId }),
                            this.$Api.Paired.getPartnerUserOptions({ spxId }),
                        ]);
                        if (isApiOk(classRes)) {
                            this.pairClassGroups = (classRes.data || []).map((g) => ({
                                schoolId: g.schoolId,
                                schoolName: g.schoolName || '',
                                classes: (g.classes || []).map((c) => ({
                                    id: c.id,
                                    name: c.name || '',
                                })),
                            }));
                        } else {
                            this.pairClassGroups = [];
                        }
                        if (isApiOk(userRes)) {
                            this.userOptions = userRes.data || [];
                        } else {
                            this.userOptions = [];
                        }
                    } else {
                        const userRes = await this.$Api.Paired.getPartnerUserOptions({ spxId });
                        this.userOptions = isApiOk(userRes) ? (userRes.data || []) : [];
                    }
                } catch (e) {
                    this.pairClassGroups = [];
                    this.userOptions = [];
                    this.$message.error('加载选项失败');
                } finally {
                    this.formOptionsLoading = false;
                }
            },
            async submitForm() {
                if (!this.form.schoolSpaceId) {
                    this.$message.warning('请选择校级空间');
                    return;
                }
                if (!this.form.name) {
                    this.$message.warning('请填写空间名称');
                    return;
                }
                if (this.isPairMode) {
                    if ((this.form.classIds || []).length < 2) {
                        this.$message.warning('结对班级空间请至少选择 2 个班级');
                        return;
                    }
                } else if (!(this.form.mentorIds || []).length || !(this.form.menteeIds || []).length) {
                    this.$message.warning('请至少各选择 1 位师傅与徒弟');
                    return;
                }

                this.formSaving = true;
                try {
                    let res;
                    if (this.isPairMode) {
                        const payload = {
                            spxId: this.editMode ? this.editingId : this.form.schoolSpaceId,
                            spxName: this.form.name,
                            classIds: this.form.classIds,
                            teacherIds: this.form.teacherIds || [],
                            createSource: this.editMode ? this.editingCreateSource : CREATE_SOURCE_PROJECT,
                        };
                        res = this.editMode
                            ? await this.$Api.Paired.updateSpxClass(payload)
                            : await this.$Api.Paired.createSpxClass(payload);
                    } else {
                        const payload = {
                            spxId: this.editMode ? this.editingId : this.form.schoolSpaceId,
                            spxName: this.form.name,
                            leaderIds: this.form.leaderIds || [],
                            masterIds: this.form.mentorIds || [],
                            apprenticeIds: this.form.menteeIds || [],
                            createSource: this.editMode ? this.editingCreateSource : CREATE_SOURCE_PROJECT,
                        };
                        res = this.editMode
                            ? await this.$Api.Paired.updateSpxPartner(payload)
                            : await this.$Api.Paired.createSpxPartner(payload);
                    }
                    if (!isApiOk(res)) {
                        this.$message.error((res && res.msg) || (this.editMode ? '保存失败' : '创建失败'));
                        return;
                    }
                    this.formDialogVisible = false;
                    this.$message.success((this.isPairMode ? '班级空间' : '研伴空间') + (this.editMode ? '已保存' : '创建成功'));
                    const parentId = this.form.schoolSpaceId;
                    await this.refreshAfterChange();
                    if (parentId) this.expandedSchoolId = parentId;
                } catch (e) {
                    this.$message.error(this.editMode ? '保存失败' : '创建失败');
                } finally {
                    this.formSaving = false;
                }
            },
            openMomentsDialog(row) {
                this.momentsTarget = row;
                this.momentsForm = {
                    id: null,
                    name: '精彩瞬间',
                    url: '',
                    syncSchool: 0,
                };
                this.momentsDialogVisible = true;
                this.fetchSpaceMoment(row);
            },
            async fetchSpaceMoment(row) {
                if (!row || !row.id) return;
                this.momentsLoading = true;
                try {
                    const res = await this.$Api.Paired.getSpaceMoment({ spxId: row.id });
                    if (!this.momentsDialogVisible) return;
                    if (!isApiOk(res)) {
                        this.$message.error((res && res.msg) || '获取精彩瞬间失败');
                        return;
                    }
                    const data = res.data;
                    if (data && data.id) {
                        this.momentsForm = {
                            id: data.id,
                            name: data.name || '精彩瞬间',
                            url: data.url || '',
                            syncSchool: Number(data.syncSchool) === 1 ? 1 : 0,
                        };
                    }
                } catch (e) {
                    if (this.momentsDialogVisible) {
                        this.$message.error('获取精彩瞬间失败');
                    }
                } finally {
                    this.momentsLoading = false;
                }
            },
            resetMomentsForm() {
                this.momentsTarget = null;
                this.momentsLoading = false;
                this.momentsSaving = false;
                this.momentsForm = {
                    id: null,
                    name: '精彩瞬间',
                    url: '',
                    syncSchool: 0,
                };
            },
            async saveMomentsConfig() {
                const name = (this.momentsForm.name || '').trim();
                if (!name) {
                    this.$message.warning('请填写名称');
                    return;
                }
                const url = (this.momentsForm.url || '').trim();
                if (!url) {
                    this.$message.warning('请填写链接地址');
                    return;
                }
                if (!this.momentsTarget || !this.momentsTarget.id) {
                    this.$message.warning('缺少空间信息');
                    return;
                }
                this.momentsSaving = true;
                try {
                    const isSchoolSpace = !!this.momentsTarget.isSchoolSpace;
                    const syncSchool = isSchoolSpace
                        ? 0
                        : (Number(this.momentsForm.syncSchool) === 1 ? 1 : 0);
                    const payload = {
                        spxId: this.momentsTarget.id,
                        name,
                        url,
                        syncSchool,
                    };
                    const isEdit = !!this.momentsForm.id;
                    if (isEdit) payload.id = this.momentsForm.id;
                    const res = isEdit
                        ? await this.$Api.Paired.updateSpaceMoment(payload)
                        : await this.$Api.Paired.createSpaceMoment(payload);
                    if (!isApiOk(res)) {
                        this.$message.error((res && res.msg) || '保存失败');
                        return;
                    }
                    this.momentsDialogVisible = false;
                    this.$message.success(!isSchoolSpace && syncSchool === 1
                        ? '精彩瞬间已保存，并将同步展示在校级空间'
                        : '精彩瞬间已保存');
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.momentsSaving = false;
                }
            },
            onDelete(row) {
                this.$confirm(`确定删除「${row.name}」吗？删除后不可恢复。`, '提示', {
                    type: 'warning',
                    confirmButtonText: '删除',
                    cancelButtonText: '取消',
                }).then(async () => {
                    try {
                        const res = this.isPairMode
                            ? await this.$Api.Paired.deleteSpxClass({ spxId: row.id })
                            : await this.$Api.Paired.deleteSpxPartner({ spxId: row.id });
                        if (!isApiOk(res)) {
                            this.$message.error((res && res.msg) || '删除失败');
                            return;
                        }
                        this.$message.success('已删除');
                        await this.refreshAfterChange();
                    } catch (e) {
                        this.$message.error('删除失败');
                    }
                }).catch(() => {});
            },
            openImportDialog() {
                this.resetImport();
                this.importDialogVisible = true;
            },
            resetImport() {
                this.importDragover = false;
                this.importFileName = '';
                this.importFile = null;
                this.importResult = null;
                this.importSaving = false;
                if (this.$refs.importFile) {
                    this.$refs.importFile.value = '';
                }
            },
            downloadTemplate() {
                // 模板放在 public/download，随前端静态资源发布（同 ydkt.dmg）
                const link = document.createElement('a');
                link.href = '/download/班级空间批量导入模板.xlsx';
                link.download = '班级空间批量导入模板.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.$message.success('模板已下载');
            },
            downloadTemplate2() {
                // 模板放在 public/download，随前端静态资源发布（同 ydkt.dmg）
                const link = document.createElement('a');
                link.href = '/download/研伴空间批量导入模板.xlsx';
                link.download = '研伴空间批量导入模板.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.$message.success('模板已下载');
            },
            onImportDrop(e) {
                this.importDragover = false;
                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                if (file) this.setImportFile(file);
            },
            onImportFileChange(e) {
                const file = e.target.files && e.target.files[0];
                if (file) this.setImportFile(file);
            },
            setImportFile(file) {
                const lower = (file.name || '').toLowerCase();
                if (!/\.xlsx$/i.test(lower)) {
                    this.$message.error('请上传 Excel 模板文件（.xlsx）');
                    return;
                }
                this.importFile = file;
                this.importFileName = file.name;
                this.importResult = null;
            },
            async confirmImport() {
                if (!this.importFile) {
                    this.$message.warning('请先选择要导入的文件');
                    return;
                }
                this.importSaving = true;
                try {
                    const res = await this.$Api.Paired.batchImport({ file: this.importFile });
                    if (!isApiOk(res)) {
                        this.$message.error((res && res.msg) || '导入失败');
                        return;
                    }
                    const result = res.data || {};
                    this.importResult = {
                        successCount: result.successCount || 0,
                        failCount: result.failCount || 0,
                        errors: result.errors || [],
                    };
                    const ok = this.importResult.successCount;
                    const fail = this.importResult.failCount;
                    if (ok > 0) {
                        this.$message.success(`成功导入 ${ok} 个子空间${fail ? `，失败 ${fail} 行` : ''}`);
                        await this.refreshAfterChange();
                    } else {
                        this.$message.warning(fail ? `导入失败 ${fail} 行` : '未导入任何数据');
                    }
                } catch (e) {
                    this.$message.error('导入失败');
                } finally {
                    this.importSaving = false;
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-light: #ECF5FF;
    @success: #67C23A;
    @success-bg: #F0F9EB;
    @danger: #F56C6C;
    @danger-bg: #FEF0F0;
    @purple: #8B5CF6;
    @purple-bg: #F5F3FF;
    @bg: #F5F7FA;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @text-placeholder: #C0C4CC;
    @border: #DCDFE6;
    @border-light: #EBEEF5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .ssm {
        min-height: 100%;
        // background: @bg;
    }

    .ssm-main-layout {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .ssm-project-name-bar {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .ssm-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .ssm-project-name-main {
        flex: 1;
        min-width: 0;
    }

    .ssm-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
    }

    .ssm-project-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    .ssm-project-type-tag {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 100px;
        font-size: 12px;
        font-weight: 600;
        background: @bg;
        color: @text-regular;

        &.pair {
            background: @primary-light;
            color: @primary;
        }

        &.yanban {
            background: @purple-bg;
            color: @purple;
        }
    }

    .ssm-project-type-hint {
        font-size: 12px;
        color: @text-secondary;
    }

    .ssm-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .ssm-unsupported {
        padding: 48px 16px;
    }

    .ssm-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 10px;
    }

    .ssm-panel-stats {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 20px;
    }

    .ssm-stat {
        font-size: 14px;
        color: @text-regular;

        em {
            font-style: normal;
            font-size: 22px;
            font-weight: 700;
            color: @text-primary;
            margin: 0 4px;
            line-height: 1;
        }
    }

    .ssm-panel-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .ssm-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 14px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s ease;
        border: 1px solid transparent;
        font-family: inherit;
        background: #fff;
    }

    .ssm-btn-sm {
        padding: 5px 10px;
        font-size: 12px;
    }

    .ssm-btn-primary {
        background: @primary;
        color: #fff;
        border-color: @primary;

        &:hover {
            background: #337ECC;
        }
    }

    .ssm-btn-default {
        color: @text-regular;
        border-color: @border;

        &:hover {
            color: @primary;
            border-color: @primary;
            background: @primary-light;
        }
    }

    .ssm-btn-danger-outline {
        color: @danger;
        border-color: #FBC4C4;

        &:hover {
            background: @danger-bg;
            border-color: @danger;
        }
    }

    .ssm-filter-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: nowrap;
        padding: 12px 16px;
        margin: 0;
        border-bottom: 1px solid @border-light;

        /deep/ .el-form-item {
            margin-bottom: 0;
            margin-right: 10px;
        }

        .ssm-filter-left {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            min-width: 0;
        }

        .ssm-filter-search {
            margin-right: 0;
            margin-left: auto;
        }

        .ssm-search-input {
            width: 360px;
        }
    }

    .ssm-panel-body {
        padding: 0;
        min-height: 200px;

        /deep/ .el-empty {
            padding: 48px 16px;
        }
    }

    .ssm-nested-scroll {
        max-height: 648px;
        overflow-y: auto;
    }

    .ssm-school-block + .ssm-school-block {
        border-top: 1px solid @border-light;
    }

    .ssm-school-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 16px 18px;
        background: #F8FBFF;
        min-height: 108px;
        box-sizing: border-box;

        &.is-expandable {
            cursor: pointer;
        }

        &.is-expandable:hover {
            background: #F0F7FF;
        }
    }

    .ssm-school-row-main {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        min-width: 0;
        flex: 1;
    }

    .ssm-expand-btn {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: @primary;
        color: #fff;
        border: none;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-top: 2px;
        box-shadow: 0 2px 6px rgba(64, 158, 255, 0.4);
        cursor: pointer;

        i {
            font-size: 12px;
            transition: transform 0.2s ease;
        }

        &.is-open i {
            transform: rotate(90deg);
        }
    }

    .ssm-expand-placeholder {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .ssm-school-info {
        min-width: 0;
        flex: 1;
    }

    .ssm-school-title-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ssm-school-type-tag {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        padding: 1px 8px;
        border-radius: 4px;
        background: @primary-light;
        color: @primary;
        font-size: 12px;
        line-height: 1.5;
    }

    .ssm-school-chips {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    .ssm-school-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
        line-height: 1.4;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 360px;
    }

    .ssm-school-chip {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 100px;
        background: @primary-light;
        color: @primary;
        font-size: 12px;
        line-height: 1.5;
        max-width: 140px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .ssm-school-admins {
        margin-top: 8px;
        font-size: 13px;
        color: @text-regular;
        line-height: 1.5;
    }

    .ssm-school-row-side {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
        flex-shrink: 0;
        text-align: right;
    }

    .ssm-school-time,
    .ssm-child-time {
        font-size: 12px;
        color: @text-placeholder;
        line-height: 1.4;
    }

    .ssm-school-child-count {
        font-size: 13px;
        color: @text-primary;
        line-height: 1.4;
    }

    .ssm-child-list {
        background: #fff;
        border-top: 1px solid @border-light;
    }

    .ssm-child-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 14px 18px 14px 52px;
        border-bottom: 1px solid @border-light;

        &:last-child {
            border-bottom: none;
        }
    }

    .ssm-child-main {
        min-width: 0;
        flex: 1;
    }

    .ssm-child-title-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
    }

    .ssm-child-title {
        font-size: 14px;
        font-weight: 600;
        color: @text-primary;
        line-height: 1.4;
    }

    .ssm-child-type-tag {
        display: inline-flex;
        align-items: center;
        padding: 1px 8px;
        border-radius: 4px;
        background: @primary-light;
        color: @primary;
        font-size: 12px;
        line-height: 1.5;
    }

    .ssm-child-meta {
        font-size: 13px;
        color: @text-regular;
        line-height: 1.7;
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .ssm-meta-label {
        font-weight: 600;
        margin-right: 6px;
        color: @text-primary;
    }

    .ssm-meta-gap {
        display: inline-block;
        width: 24px;
        height: 1px;
        flex-shrink: 0;
    }

    .ssm-child-side {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
        flex-shrink: 0;
    }

    .ssm-child-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ssm-child-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 5px 12px;
        border-radius: 6px;
        border: none;
        background: @primary;
        color: #fff;
        font-size: 12px;
        font-family: inherit;
        cursor: pointer;
        line-height: 1.4;
        white-space: nowrap;

        &:hover {
            background: #337ECC;
        }
    }

    .ssm-radio-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;

        /deep/ .el-radio {
            margin-right: 0;
            font-weight: 400;
            color: @text-regular;
        }
    }

    .ssm-pagination {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-top: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 8px;
    }

    .ssm-pagination-info {
        font-size: 13px;
        color: @text-secondary;
    }

    .ssm-required {
        color: @danger;
    }

    .ssm-form-hint {
        font-size: 12px;
        color: @text-secondary;
        margin: 0 0 8px;
        line-height: 1.5;
    }

    .ssm-form-hint-after {
        margin: 6px 0 0;
    }

    .ssm-moments-hint {
        margin-bottom: 14px;
    }

    .ssm-check-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        max-height: 180px;
        overflow-y: auto;
        padding: 4px;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: @bg;
    }

    .ssm-check-item {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 8px 10px;
        background: #fff;
        border-radius: 6px;
        border: 1px solid @border-light;
        font-size: 12px;
        cursor: pointer;
        margin: 0;

        &:hover {
            border-color: @primary;
        }

        /deep/ .el-checkbox {
            display: flex;
            align-items: flex-start;
            white-space: normal;
            line-height: 1.4;
        }

        /deep/ .el-checkbox__label {
            white-space: normal;
            line-height: 1.4;
            font-size: 12px;
            color: @text-regular;
        }
    }

    .ssm-import-steps {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .ssm-import-step {
        padding: 14px 16px;
        border: 1px solid @border-light;
        border-radius: 8px;
        background: @bg;
    }

    .ssm-import-step-title {
        font-size: 13px;
        font-weight: 700;
        color: @text-primary;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ssm-import-step-num {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: @primary;
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .ssm-import-desc {
        font-size: 12px;
        color: @text-secondary;
        margin: 0 0 10px;
        line-height: 1.6;
    }

    .ssm-import-tpl-fields {
        font-size: 12px;
        color: @text-regular;
        line-height: 1.7;
        margin: 0;
        padding-left: 18px;
    }

    .ssm-upload-zone {
        border: 1.5px dashed @border;
        border-radius: 8px;
        background: #fff;
        padding: 28px 16px;
        text-align: center;
        cursor: pointer;
        transition: all 0.25s ease;

        &:hover,
        &.dragover {
            border-color: @primary;
            background: @primary-light;
        }
    }

    .ssm-uz-icon {
        font-size: 28px;
        margin-bottom: 8px;
    }

    .ssm-uz-title {
        font-size: 13px;
        font-weight: 600;
        color: @text-primary;
    }

    .ssm-uz-hint {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 4px;
    }

    .ssm-uz-file {
        font-size: 12px;
        color: @primary;
        margin-top: 8px;
        font-weight: 500;
        word-break: break-all;
    }

    .ssm-import-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 10px;
        font-size: 12px;
    }

    .ssm-sum-item {
        padding: 4px 10px;
        border-radius: 100px;
        background: #fff;
        border: 1px solid @border-light;
        color: @text-regular;

        &.ok {
            background: @success-bg;
            color: @success;
            border-color: transparent;
        }

        &.err {
            background: @danger-bg;
            color: @danger;
            border-color: transparent;
        }
    }

    .ssm-import-preview-wrap {
        max-height: 220px;
        overflow: auto;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: #fff;
    }

    .ssm-import-empty {
        padding: 20px;
        text-align: center;
        color: @text-secondary;
        font-size: 12px;
    }

    .ssm-import-preview {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;

        th {
            position: sticky;
            top: 0;
            background: @bg;
            text-align: left;
            padding: 8px 10px;
            font-weight: 600;
            color: @text-regular;
            border-bottom: 1px solid @border-light;
        }

        td {
            padding: 8px 10px;
            border-bottom: 1px solid @border-light;
            vertical-align: top;
            color: @text-regular;
        }

        tr.is-invalid td {
            background: #FFF5F5;
        }

        .row-ok {
            color: @success;
            font-weight: 600;
        }

        .row-err {
            color: @danger;
            font-weight: 600;
        }
    }


    .ssm-class-groups {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .ssm-class-group-title {
        font-size: 12px;
        font-weight: 600;
        color: @text-regular;
        margin-bottom: 6px;
    }

    @media (max-width: 768px) {
        .ssm-filter-bar {
            flex-wrap: wrap;

            .ssm-filter-left {
                flex-wrap: wrap;
            }

            .ssm-filter-search {
                margin-left: 0;
                width: 100%;
            }

            .ssm-search-input {
                width: 100%;
            }
        }

        .ssm-school-row,
        .ssm-child-row {
            flex-direction: column;
        }

        .ssm-school-row-side,
        .ssm-child-side {
            align-items: flex-start;
            width: 100%;
        }

        .ssm-child-row {
            padding-left: 18px;
        }

        .ssm-check-grid {
            grid-template-columns: 1fr;
        }
    }
</style>

<style lang="less">

    .ssm-form-dialog,
    .ssm-import-dialog,
    .ssm-moments-dialog {
        .el-dialog__body {
            padding-top: 10px;
        }

        .el-form-item__label {
            font-size: 13px;
            font-weight: 600;
            color: #303133;
            padding-bottom: 0;
            line-height: 1.4;
        }

        .el-form-item {
            margin-bottom: 16px;
        }
    }

    .ssm-moments-dialog .el-form-item__label {
        margin-bottom: 16px;
    }
</style>
